/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import DiscussionsUserPage from 'flarum/forum/components/DiscussionsUserPage';
import ReviewListState from '../states/ReviewListState';
import ReviewList from './ReviewList';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import listItems from 'flarum/common/helpers/listItems';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import Switch from 'flarum/common/components/Switch';
import Tooltip from 'flarum/common/components/Tooltip';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;

const { findChild } = utils.vdom;
import type { VdomParent } from '@glowingblue-orell-thalia-common/utils/vdom';
import { startReviewsTour } from '../extend/reviewsTour';
import Stream from 'flarum/common/utils/Stream';
import ReviewsExportStatus from './ReviewsExportStatus';
import type Mithril from 'mithril';
import type User from 'flarum/common/models/User';
import UserPage from 'flarum/forum/components/UserPage';

export default class ReviewsUserPage extends DiscussionsUserPage {
	state!: ReviewListState;

	/** Whether the reviews belong to the acting user, who may then filter and export them. */
	showFilter: boolean | null = null;

	toggleAllLoading: Stream<boolean>;
	hideExportBtnTooltip: Stream<boolean>;

	show(user: User) {
		UserPage.prototype.show.call(this, user);

		this.state = new ReviewListState({
			filter: { author: user.username() },
			sort: 'newest',
		});

		this.state.refresh();

		this.showFilter = app.session.user && app.session.user.id() === this.user!.id();

		this.toggleAllLoading = Stream(false);
		this.hideExportBtnTooltip = Stream(false);
	}

	view() {
		const vnode = super.view();

		if (this.showFilter) {
			// `findChild` returns the generic vdom shape; narrowed to what this mutation relies on.
			const userPage = findChild(vnode as VdomParent, 'UserPage', true) as { attrs: { className: string } } | null;
			if (userPage) {
				userPage.attrs.className += ' ReviewsUserPage--withExportActions';
			}
		}

		return vnode;
	}

	content() {
		return (
			<div className="ReviewsUserPage">
				{this.showFilter && (
					<>
						<div className="ReviewsUserPage-toolbar IndexPage-toolbar">
							<ul className="IndexPage-toolbar-view">{listItems(this.viewItems().toArray())}</ul>
							<ul className="IndexPage-toolbar-action">{listItems(this.actionItems().toArray())}</ul>
						</div>
						<div className="ReviewsUserPage-toolbar-status">
							<ReviewsExportStatus state={this.state} />
						</div>
					</>
				)}
				<ReviewList state={this.state} showExport={this.showFilter} />
			</div>
		);
	}

	viewItems() {
		const items = new ItemList<Mithril.Children>();

		const filterKeys = this.state.filterKeys;

		// `filterKeys` resolves to
		// glowingblue-orell-rezensionen.forum.user.reviews.filter.{all,exported,not_exported}.
		const filterOptions = filterKeys.reduce<Record<string, Mithril.Children>>((acc, filterKey) => {
			acc[filterKey] = app.translator.trans(`glowingblue-orell-rezensionen.forum.user.reviews.filter.${filterKey}`);
			return acc;
		}, {});

		items.add(
			'filter',
			<Dropdown
				buttonClassName="Button"
				label={filterOptions[app.search.params().filter] || filterOptions[filterKeys[0]]}
				accessibleToggleLabel={app.translator.trans('core.forum.index_sort.toggle_dropdown_accessible_label')}
			>
				{filterKeys.map((key) => {
					const label = filterOptions[key];
					const active = (app.search.params().filter || filterKeys[0]) === key;

					return (
						<Button icon={active ? 'fas fa-check' : true} onclick={this.state.changeFilter.bind(this.state, key)} active={active}>
							{label}
						</Button>
					);
				})}
			</Dropdown>
		);

		return items;
	}

	actionItems() {
		const items = new ItemList<Mithril.Children>();

		items.add(
			'tour',
			<Button
				className="Button Button--icon Button--link ReviewsUserPage-tourButton"
				icon="far fa-question-circle"
				onclick={() => startReviewsTour()}
			/>
		);

		items.add(
			'export',
			<Tooltip
				text={app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_tooltip', { selected: this.state.selectedCount })}
				tooltipVisible={!this.state.selectedCount || this.state.allExported || this.hideExportBtnTooltip() ? false : undefined}
				key={String(this.state.selectedCount && !this.state.allExported && !this.hideExportBtnTooltip())}
			>
				<Button
					icon="fa-kit fa-regular-cloud-circle-arrow-up"
					className="Button Button--primary"
					disabled={!this.state.selectedCount || this.state.allExported}
					onclick={() => {
						// Hide the tooltip for the duration of the export, or it overlaps the UI.
						this.hideExportBtnTooltip(true);

						this.state.export();

						setTimeout(() => {
							this.hideExportBtnTooltip(false);
							m.redraw();
						}, 0);
					}}
				>
					{this.state.selectedCount && !this.state.allExported
						? app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_label')
						: app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_label_disabled')}
				</Button>
			</Tooltip>
		);

		const tooltipVisible = this.state.allExported || this.toggleAllLoading() ? false : undefined;

		let toggleAllText = app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.select_all_tooltip');
		if (tooltipVisible === false) {
			toggleAllText = '';
		}

		items.add(
			'toggle-all',
			<Tooltip
				text={toggleAllText}
				tooltipVisible={tooltipVisible}
				key={String(this.state.allSelected && !this.state.allExported && tooltipVisible) + String(Math.random())}
			>
				<Switch
					icon="fa-kit fa-regular-cloud-circle-arrow-up"
					state={(this.state.allSelected && !this.state.allExported) || this.state.exportingInProgress}
					disabled={this.state.allExported || this.toggleAllLoading() || !this.state.selectableCount || this.state.exportingInProgress}
					loading={this.toggleAllLoading()}
					onchange={async (selected: boolean) => {
						this.toggleAllLoading(true);
						m.redraw();

						// Not `linkedShop()`: it returns false after an interrupted authentication.
						if (!app.session.user!.attribute('linkedShop')) {
							// The linking may be interrupted, so don't spin forever.
							this.toggleAllLoading(false);
							m.redraw();
							await Auth.linkAccount(
								app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_connect'),
								ReviewListState.gaExportAttrs
							);
							this.toggleAllLoading(true);
							m.redraw();
						}

						await this.state.selectAll(selected);

						setTimeout(() => {
							this.toggleAllLoading(false);
							m.redraw();
						});
					}}
				/>
			</Tooltip>
		);

		return items;
	}
}
